/**
 * Character system — single source of truth for story protagonist data.
 *
 * Source JSON: docs/kiddoware_ai_story_protagonists.json
 * Never hardcode character names, species, or sidekick names anywhere in
 * engine or player code — always import from here.
 */
import data from '../../docs/kiddoware_ai_story_protagonists.json';

const storyUniverse = data.story_universe;

/** @param {'male' | 'female' | string | undefined} gender */
function pronounsFromGender(gender) {
  if (gender === 'female') {
    return { pronoun: 'she', possessive: 'her', object: 'her' };
  }
  return { pronoun: 'he', possessive: 'his', object: 'him' };
}

function withPronouns(char) {
  if (!char) return null;
  return { ...char, ...pronounsFromGender(char.gender) };
}

export const PEBBLES = withPronouns(storyUniverse.protagonists.find((p) => p.name === 'Pebbles'));
export const MILO = withPronouns(storyUniverse.protagonists.find((p) => p.name === 'Milo'));
export const VECTOR = withPronouns(storyUniverse.protagonists.find((p) => p.name === 'Vector'));

export const CHARACTERS = [PEBBLES, MILO, VECTOR];

/**
 * Case-insensitive look-up of a protagonist by name.
 * Returns null when the name does not match any known character.
 */
export function getCharacter(name) {
  if (!name) return null;
  const lower = String(name).toLowerCase();
  const char = CHARACTERS.find((c) => c.name.toLowerCase() === lower);
  return char ?? null;
}

/**
 * Subject / possessive / object pronouns for a protagonist name.
 * Returns null when the character is unknown.
 */
export function getCharacterPronouns(name) {
  const char = getCharacter(name);
  if (!char) return null;
  return {
    pronoun: char.pronoun,
    possessive: char.possessive,
    object: char.object,
  };
}

/**
 * Returns the sidekicks array for a given protagonist name.
 * Returns [] when the character is unknown.
 */
export function getSidekicks(characterName) {
  const char = getCharacter(characterName);
  return char?.sidekicks ?? [];
}
